import React, { useCallback, useEffect, useState } from 'react'
import NextLink from 'next/link'
import { useRouter } from 'next/router'
import { Button, Link } from '@alto-avios/alto-ui'
import { ContentfulCta as ContentfulCtaType } from '@reward-platform/contentful'
import { usePlatformRoutes } from '~/context/platform'
import { useTagManagerContext } from '~/context/analytics'
import { normalisePathname } from '~/utils/normalisePathname'
import {
  ContentfulCtaEventProps,
  CTA_EVENT_NAME,
  generateContentfulCtaGTMEventData,
} from './eventTracking'

type ContentfulCtaProps = Pick<ContentfulCtaType, 'link' | 'label' | 'variant'> & {
  size?: 'sm' | 'md' | 'lg'
  ariaLabel?: string
  eventData: ContentfulCtaEventProps
}

const URL_PARSE_BASE = 'https://avios.com'

const isAbsoluteHttpLink = (url: string) => {
  return /^(http|https):\/\//.test(url)
}

const getUrlPath = (url: URL) => `${url.pathname}${url.search}${url.hash}`

const createInternalRoutePath = (
  link: string,
  createRoutePath: (path: string) => string
): string => {
  if (!link.startsWith('/')) {
    return createRoutePath(link)
  }

  const url = new URL(link, URL_PARSE_BASE)
  return `${createRoutePath(url.pathname)}${url.search}${url.hash}`
}

const getLinkNavigation = (
  link: string,
  browserOrigin: string | undefined,
  createRoutePath: (path: string) => string
) => {
  if (!isAbsoluteHttpLink(link)) {
    return { externalLink: false, linkPath: createInternalRoutePath(link, createRoutePath) }
  }

  const absoluteLink = new URL(link)
  if (absoluteLink.origin === browserOrigin) {
    return { externalLink: false, linkPath: getUrlPath(absoluteLink) }
  }

  return { externalLink: true, linkPath: link }
}

const isPathSamePage = (linkPath: string, currentPath: string) => {
  const linkUrl = new URL(linkPath, URL_PARSE_BASE)
  const currentUrl = new URL(currentPath, URL_PARSE_BASE)
  return normalisePathname(linkUrl.pathname) === normalisePathname(currentUrl.pathname)
}

const ContentfulCta = ({
  link,
  label,
  variant,
  size,
  ariaLabel,
  eventData,
}: ContentfulCtaProps) => {
  const router = useRouter()
  const { createRoutePath } = usePlatformRoutes()
  const { pushEventToDataLayer } = useTagManagerContext()
  const [isHydrated, setIsHydrated] = useState(false)

  useEffect(() => {
    setIsHydrated(true)
  }, [])

  const handleClick = useCallback(() => {
    const eventDataLayerObject = generateContentfulCtaGTMEventData(label, eventData)
    pushEventToDataLayer(CTA_EVENT_NAME, eventDataLayerObject)
  }, [eventData, label, pushEventToDataLayer])

  if (!link || !label) {
    return null
  }

  const browserOrigin = isHydrated ? globalThis.location.origin : undefined
  const { externalLink, linkPath } = getLinkNavigation(link, browserOrigin, createRoutePath)
  const currentRoutePath = createInternalRoutePath(router.asPath, createRoutePath)
  const isSamePageLink =
    !externalLink &&
    (isPathSamePage(linkPath, router.asPath) || isPathSamePage(linkPath, currentRoutePath))
  const opensInNewTab = !isSamePageLink
  const linkProps = opensInNewTab ? { target: '_blank' as const, rel: 'noopener noreferrer' } : {}

  if (variant === 'link') {
    const ctaLink = (
      <Link
        href={linkPath}
        aria-label={ariaLabel}
        {...linkProps}
        onClick={() => handleClick()}
        size={size}
        iconEndProps={isHydrated ? undefined : false}
      >
        {label}
      </Link>
    )

    if (!isSamePageLink) {
      return ctaLink
    }

    return (
      <NextLink href={linkPath} shallow scroll={false} passHref legacyBehavior>
        {ctaLink}
      </NextLink>
    )
  }

  const ctaButton = (
    <Button
      as="a"
      elementType="a"
      emphasis={variant}
      size={size}
      aria-label={ariaLabel}
      onPress={() => handleClick()}
      passthroughProps={{
        href: linkPath,
        ...(opensInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {}),
      }}
    >
      {label}
    </Button>
  )

  if (opensInNewTab) {
    return ctaButton
  }

  return (
    <NextLink href={linkPath} shallow={isSamePageLink} scroll={false} passHref legacyBehavior>
      {ctaButton}
    </NextLink>
  )
}

export default ContentfulCta
